import { useState, useEffect, useCallback, useMemo } from "react"
import {
  Container,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Input,
  IconButton,
  Tooltip,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  FormHelperText,
  Textarea,
  Select,
  Option,
  Sheet,
  Table,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Alert
} from "@mui/joy"
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Article as TemplateIcon,
  Email as EmailIcon
} from "@mui/icons-material"
import { toast } from "sonner"
import api from "../services/api"

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

type TemplateType = "campaign" | "tx"
type TemplateStatus = "draft" | "active" | "archived"

interface EmailTemplate {
  id: number
  companyId: number
  name: string
  subject: string
  previewText?: string | null
  htmlContent: string
  textContent?: string | null
  type: TemplateType
  category: string
  status: TemplateStatus
  tags?: string[]
  provider?: string | null
  providerTemplateId?: string | null
  createdAt?: string
  updatedAt?: string
}

interface ListResp {
  records: EmailTemplate[]
  count: number
  hasMore: boolean
}

interface FormState {
  id: number | null
  name: string
  subject: string
  previewText: string
  htmlContent: string
  textContent: string
  type: TemplateType
  category: string
  status: TemplateStatus
  tagsCsv: string
}

const EMPTY_FORM: FormState = {
  id: null,
  name: "",
  subject: "",
  previewText: "",
  htmlContent: "",
  textContent: "",
  type: "campaign",
  category: "general",
  status: "draft",
  tagsCsv: ""
}

const TEMPLATE_VARIABLES = [
  { key: "{{nombre}}", desc: "Nombre del suscriptor" },
  { key: "{{empresa}}", desc: "Tu empresa" },
  { key: "{{enlace_accion}}", desc: "URL del CTA principal" },
  { key: "{{enlace_unsub}}", desc: "URL para desuscribirse" }
]

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export default function EmailTemplatesEditor() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [count, setCount] = useState(0)
  const [searchParam, setSearchParam] = useState("")
  const [pageNumber, setPageNumber] = useState(1)
  const [typeFilter, setTypeFilter] = useState<"" | TemplateType>("")
  const [statusFilter, setStatusFilter] = useState<"" | TemplateStatus>("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [editorTab, setEditorTab] = useState<"editor" | "preview" | "variables">("editor")
  const [saving, setSaving] = useState(false)

  const [testOpen, setTestOpen] = useState(false)
  const [testEmail, setTestEmail] = useState("")
  const [testFromEmail, setTestFromEmail] = useState("")
  const [testFromName, setTestFromName] = useState("")
  const [testTemplateId, setTestTemplateId] = useState<number | null>(null)
  const [testing, setTesting] = useState(false)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, string | number> = {
        searchParam,
        pageNumber
      }
      if (typeFilter) params.type = typeFilter
      if (statusFilter) params.status = statusFilter

      const { data } = await api.get<ListResp>("/email-templates", { params })
      setTemplates(data.records || [])
      setCount(data.count || 0)
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      setError(ax.response?.data?.message || ax.message || "Error al cargar plantillas")
    } finally {
      setLoading(false)
    }
  }, [searchParam, pageNumber, typeFilter, statusFilter])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  // -------------------------------------------------------------------------
  // Handlers CRUD
  // -------------------------------------------------------------------------

  const openNew = () => {
    setForm({ ...EMPTY_FORM })
    setEditorTab("editor")
    setEditorOpen(true)
  }

  const openEdit = (tpl: EmailTemplate) => {
    setForm({
      id: tpl.id,
      name: tpl.name,
      subject: tpl.subject,
      previewText: tpl.previewText || "",
      htmlContent: tpl.htmlContent,
      textContent: tpl.textContent || "",
      type: tpl.type === "tx" ? "tx" : "campaign",
      category: tpl.category || "general",
      status: tpl.status,
      tagsCsv: (tpl.tags || []).join(", ")
    })
    setEditorTab("editor")
    setEditorOpen(true)
  }

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.htmlContent) {
      toast.error("Nombre, asunto y contenido HTML son obligatorios")
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: form.name,
        subject: form.subject,
        previewText: form.previewText,
        htmlContent: form.htmlContent,
        textContent: form.textContent,
        type: form.type,
        category: form.category,
        status: form.status,
        tags: form.tagsCsv.split(",").map(s => s.trim()).filter(Boolean)
      }

      if (form.id) {
        await api.put(`/email-templates/${form.id}`, payload)
        toast.success("Plantilla actualizada")
      } else {
        await api.post("/email-templates", payload)
        toast.success("Plantilla creada")
      }
      setEditorOpen(false)
      await fetchTemplates()
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error al guardar")
    } finally {
      setSaving(false)
    }
  }

  const handleDuplicate = async (id: number) => {
    try {
      await api.post(`/email-templates/${id}/duplicate`)
      toast.success("Plantilla duplicada")
      await fetchTemplates()
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error al duplicar")
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm("¿Archivar esta plantilla? (se mantiene en BD, no se borra)")) return
    try {
      await api.delete(`/email-templates/${id}`)
      toast.success("Plantilla archivada")
      await fetchTemplates()
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error al archivar")
    }
  }

  const openTest = (id: number) => {
    setTestTemplateId(id)
    setTestEmail("")
    setTestFromEmail("")
    setTestFromName("")
    setTestOpen(true)
  }

  const handleSendTest = async () => {
    if (!testTemplateId || !testEmail) {
      toast.error("Email destinatario es obligatorio")
      return
    }
    setTesting(true)
    try {
      await api.post(`/email-templates/${testTemplateId}/test-send`, {
        to: testEmail,
        fromEmail: testFromEmail || undefined,
        fromName: testFromName || undefined
      })
      toast.success(`Prueba enviada a ${testEmail}`)
      setTestOpen(false)
    } catch (err) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(ax.response?.data?.message || ax.message || "Error al enviar prueba")
    } finally {
      setTesting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const previewSrcDoc = useMemo(() => {
    return form.htmlContent
  }, [form.htmlContent])

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 3, gap: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "md",
              bgcolor: "primary.softBg",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <TemplateIcon sx={{ color: "primary.plainColor", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography level="h3" sx={{ fontWeight: 700 }}>
              Plantillas de Email
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary" }}>
              Crea y gestiona plantillas para campanas masivas (sync con tu provider activo)
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Recargar">
            <IconButton variant="outlined" color="neutral" onClick={fetchTemplates} disabled={loading} size="sm">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button startDecorator={<AddIcon />} onClick={openNew} color="primary">
            Nueva plantilla
          </Button>
        </Stack>
      </Stack>

      {/* Filtros */}
      <Card variant="outlined" sx={{ mb: 2.5, borderRadius: "lg" }}>
        <CardContent>
          <Grid container spacing={1.5} alignItems="end">
            <Grid xs={12} md={5}>
              <FormControl size="sm">
                <FormLabel>Buscar</FormLabel>
                <Input
                  size="sm"
                  startDecorator={<SearchIcon fontSize="small" />}
                  placeholder="Buscar por nombre..."
                  value={searchParam}
                  onChange={e => {
                    setSearchParam(e.target.value)
                    setPageNumber(1)
                  }}
                />
              </FormControl>
            </Grid>
            <Grid xs={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Tipo</FormLabel>
                <Select
                  size="sm"
                  value={typeFilter}
                  onChange={(_e, v) => {
                    setTypeFilter((v || "") as "" | TemplateType)
                    setPageNumber(1)
                  }}
                >
                  <Option value="">Todos</Option>
                  <Option value="campaign">Campana</Option>
                  <Option value="tx">Transaccional</Option>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={6} md={3}>
              <FormControl size="sm">
                <FormLabel>Estado</FormLabel>
                <Select
                  size="sm"
                  value={statusFilter}
                  onChange={(_e, v) => {
                    setStatusFilter((v || "") as "" | TemplateStatus)
                    setPageNumber(1)
                  }}
                >
                  <Option value="">Todos</Option>
                  <Option value="draft">Borrador</Option>
                  <Option value="active">Activa</Option>
                  <Option value="archived">Archivada</Option>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert color="danger" variant="soft">{error}</Alert>
      ) : templates.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: "lg", textAlign: "center", py: 6 }}>
          <CardContent>
            <TemplateIcon sx={{ fontSize: 52, color: "neutral.plainColor", mb: 2, opacity: 0.6 }} />
            <Typography level="title-md" sx={{ mb: 1 }}>
              Sin plantillas todavia
            </Typography>
            <Typography level="body-sm" sx={{ color: "text.secondary", mb: 2 }}>
              Crea tu primera plantilla para usar en campanas
            </Typography>
            <Button startDecorator={<AddIcon />} onClick={openNew}>
              Crear plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: "lg", overflow: "auto" }}>
          <Table hoverRow size="sm" stickyHeader>
            <thead>
              <tr>
                <th style={{ minWidth: 200 }}>Nombre</th>
                <th style={{ minWidth: 220 }}>Asunto</th>
                <th style={{ width: 110 }}>Tipo</th>
                <th style={{ width: 110 }}>Estado</th>
                <th style={{ width: 110 }}>Provider</th>
                <th style={{ width: 200 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(tpl => (
                <tr key={tpl.id}>
                  <td>
                    <Stack spacing={0.25}>
                      <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {tpl.name}
                      </Typography>
                      {tpl.previewText && (
                        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                          {tpl.previewText.slice(0, 50)}...
                        </Typography>
                      )}
                    </Stack>
                  </td>
                  <td>
                    <Typography level="body-xs">{tpl.subject}</Typography>
                  </td>
                  <td>
                    <Chip size="sm" variant="soft" color={tpl.type === "tx" ? "warning" : "primary"}>
                      {tpl.type === "tx" ? "TX" : "Campana"}
                    </Chip>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={
                        tpl.status === "active" ? "success" :
                        tpl.status === "archived" ? "neutral" : "warning"
                      }
                    >
                      {tpl.status}
                    </Chip>
                  </td>
                  <td>
                    {tpl.provider ? (
                      <Chip size="sm" variant="outlined" color="primary">
                        {tpl.provider}
                      </Chip>
                    ) : (
                      <Chip size="sm" variant="soft" color="neutral">No sync</Chip>
                    )}
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Editar">
                        <IconButton size="sm" variant="outlined" color="primary" onClick={() => openEdit(tpl)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicar">
                        <IconButton size="sm" variant="outlined" color="neutral" onClick={() => handleDuplicate(tpl.id)}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Enviar prueba">
                        <IconButton size="sm" variant="outlined" color="success" onClick={() => openTest(tpl.id)}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Archivar">
                        <IconButton size="sm" variant="outlined" color="danger" onClick={() => handleDelete(tpl.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Footer paginacion simple */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          Total: {count}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="sm"
            variant="outlined"
            disabled={pageNumber <= 1 || loading}
            onClick={() => setPageNumber(p => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <Typography level="body-sm" sx={{ alignSelf: "center" }}>
            Pagina {pageNumber}
          </Typography>
          <Button
            size="sm"
            variant="outlined"
            disabled={loading || pageNumber * 20 >= count}
            onClick={() => setPageNumber(p => p + 1)}
          >
            Siguiente
          </Button>
        </Stack>
      </Stack>

      {/* ---------------- Editor modal ---------------- */}
      <Modal open={editorOpen} onClose={() => setEditorOpen(false)}>
        <ModalDialog
          size="lg"
          sx={{ width: "min(1100px, 95vw)", maxHeight: "92vh", overflow: "auto" }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1 }}>
            {form.id ? "Editar plantilla" : "Nueva plantilla"}
          </Typography>

          <Tabs
            value={editorTab}
            onChange={(_e, v) => setEditorTab((v || "editor") as typeof editorTab)}
            sx={{ mb: 1.5 }}
          >
            <TabList>
              <Tab value="editor">
                <EditIcon sx={{ fontSize: 16, mr: 0.5 }} /> Editor
              </Tab>
              <Tab value="preview">
                <PreviewIcon sx={{ fontSize: 16, mr: 0.5 }} /> Preview
              </Tab>
              <Tab value="variables">Variables</Tab>
            </TabList>

            <TabPanel value="editor" sx={{ p: 0, pt: 1.5 }}>
              <Grid container spacing={1.5}>
                <Grid xs={12} md={6}>
                  <FormControl size="sm" required>
                    <FormLabel>Nombre</FormLabel>
                    <Input
                      size="sm"
                      placeholder="Newsletter Octubre 2026"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={6}>
                  <FormControl size="sm" required>
                    <FormLabel>Asunto</FormLabel>
                    <Input
                      size="sm"
                      placeholder="Hola {{nombre}}, novedades de {{empresa}}"
                      value={form.subject}
                      onChange={e => setForm({ ...form, subject: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl size="sm">
                    <FormLabel>Preview text</FormLabel>
                    <Input
                      size="sm"
                      placeholder="Texto que aparece como vista previa en la bandeja"
                      value={form.previewText}
                      onChange={e => setForm({ ...form, previewText: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl size="sm">
                    <FormLabel>Tipo</FormLabel>
                    <Select
                      size="sm"
                      value={form.type}
                      onChange={(_e, v) => setForm({ ...form, type: (v || "campaign") as TemplateType })}
                    >
                      <Option value="campaign">Campana (masiva)</Option>
                      <Option value="tx">Transaccional</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl size="sm">
                    <FormLabel>Categoria</FormLabel>
                    <Select
                      size="sm"
                      value={form.category}
                      onChange={(_e, v) => setForm({ ...form, category: v || "general" })}
                    >
                      <Option value="general">General</Option>
                      <Option value="newsletter">Newsletter</Option>
                      <Option value="promotional">Promocional</Option>
                      <Option value="welcome">Bienvenida</Option>
                      <Option value="transactional">Transaccional</Option>
                      <Option value="event">Evento</Option>
                      <Option value="ecommerce">E-commerce</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} md={4}>
                  <FormControl size="sm">
                    <FormLabel>Estado</FormLabel>
                    <Select
                      size="sm"
                      value={form.status}
                      onChange={(_e, v) => setForm({ ...form, status: (v || "draft") as TemplateStatus })}
                    >
                      <Option value="draft">Borrador</Option>
                      <Option value="active">Activa (sync con provider)</Option>
                      <Option value="archived">Archivada</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl size="sm" required>
                    <FormLabel>HTML</FormLabel>
                    <Textarea
                      size="sm"
                      minRows={14}
                      maxRows={20}
                      sx={{ fontFamily: "monospace", fontSize: 12 }}
                      placeholder="<div>...</div>"
                      value={form.htmlContent}
                      onChange={e => setForm({ ...form, htmlContent: e.target.value })}
                    />
                    <FormHelperText>
                      Usa variables como {"{{nombre}}"} y {"{{enlace_unsub}}"}. El render se hace en el provider.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl size="sm">
                    <FormLabel>Texto plano (opcional)</FormLabel>
                    <Textarea
                      size="sm"
                      minRows={3}
                      maxRows={6}
                      placeholder="Version texto para clientes que no rendericen HTML"
                      value={form.textContent}
                      onChange={e => setForm({ ...form, textContent: e.target.value })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl size="sm">
                    <FormLabel>Tags (separados por coma)</FormLabel>
                    <Input
                      size="sm"
                      placeholder="newsletter, octubre"
                      value={form.tagsCsv}
                      onChange={e => setForm({ ...form, tagsCsv: e.target.value })}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value="preview" sx={{ p: 0, pt: 1.5 }}>
              <Stack spacing={1.5}>
                <Alert color="primary" variant="soft">
                  <Box>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {form.subject || "(sin asunto)"}
                    </Typography>
                    {form.previewText && (
                      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {form.previewText}
                      </Typography>
                    )}
                  </Box>
                </Alert>
                <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "hidden", height: "60vh" }}>
                  <iframe
                    title="preview"
                    srcDoc={previewSrcDoc}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      backgroundColor: "#fff"
                    }}
                  />
                </Sheet>
              </Stack>
            </TabPanel>

            <TabPanel value="variables" sx={{ p: 0, pt: 1.5 }}>
              <Stack spacing={1.5}>
                <Alert color="primary" variant="soft" startDecorator={<EmailIcon />}>
                  <Typography level="body-sm">
                    Variables comunes que el provider sustituye al renderizar
                  </Typography>
                </Alert>
                <Sheet variant="outlined" sx={{ borderRadius: "md" }}>
                  <Table size="sm" hoverRow>
                    <thead>
                      <tr>
                        <th style={{ width: 200 }}>Variable</th>
                        <th>Descripcion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {TEMPLATE_VARIABLES.map(v => (
                        <tr key={v.key}>
                          <td>
                            <code style={{ fontFamily: "monospace" }}>{v.key}</code>
                          </td>
                          <td>{v.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </Stack>
            </TabPanel>
          </Tabs>

          <Divider sx={{ my: 1.5 }} />

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" color="neutral" onClick={() => setEditorOpen(false)} startDecorator={<CloseIcon />}>
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={saving}
              startDecorator={saving ? <CircularProgress size="sm" /> : <SaveIcon />}
            >
              {saving ? "Guardando..." : form.id ? "Actualizar" : "Crear"}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* ---------------- Test send modal ---------------- */}
      <Modal open={testOpen} onClose={() => setTestOpen(false)}>
        <ModalDialog size="md">
          <ModalClose />
          <Typography level="h4" sx={{ mb: 1 }}>
            Enviar prueba
          </Typography>
          <Stack spacing={1.5}>
            <FormControl size="sm" required>
              <FormLabel>Email destinatario</FormLabel>
              <Input
                size="sm"
                placeholder="tu-email@dominio.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
              />
            </FormControl>
            <FormControl size="sm">
              <FormLabel>Email remitente (opcional)</FormLabel>
              <Input
                size="sm"
                placeholder="marketing@chateam.ws"
                value={testFromEmail}
                onChange={e => setTestFromEmail(e.target.value)}
              />
            </FormControl>
            <FormControl size="sm">
              <FormLabel>Nombre remitente (opcional)</FormLabel>
              <Input
                size="sm"
                placeholder="ChatEAM Marketing"
                value={testFromName}
                onChange={e => setTestFromName(e.target.value)}
              />
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
              <Button variant="outlined" color="neutral" onClick={() => setTestOpen(false)}>
                Cancelar
              </Button>
              <Button
                color="primary"
                onClick={handleSendTest}
                disabled={testing}
                startDecorator={testing ? <CircularProgress size="sm" /> : <SendIcon />}
              >
                {testing ? "Enviando..." : "Enviar prueba"}
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Container>
  )
}
